'use client';

import { Button, Feature, Heading, LayoutContainer, Picture, TextPassage } from '@petsmart-ui/sparky';
import ReactPlayer from 'react-player';

import { trackFeatureBannerClick } from '@libs/analytics';
import { DEVICE_TYPE, useDeviceType } from '@libs/common-next';
import {
    COLOR_TABLE,
    NEW_TAB,
    PRIMARY_BUTTON_STYLE,
    SECONDARY_BUTTON_STYLE,
    TARGET_BLANK,
    TARGET_SELF,
} from '../../constants';
import type { FeatureBannerProps } from './types';

import './FeatureBanner.scss';

const IMG_SIZE_FEATURE_BANNER = {
    width: 720,
    height: 400,
};

export default function FeatureBanner({
    header,
    description,
    cta,
    media,
    textColor,
    backgroundColor,
    isH1,
}: Readonly<FeatureBannerProps>) {
    const isMobile = useDeviceType() === DEVICE_TYPE.MOBILE;

    const style = {
        backgroundColor,
        color: COLOR_TABLE[textColor],
    };

    const buttonStyle = cta?.buttonStyle?.toLowerCase();
    const variant = buttonStyle === 'primary' ? PRIMARY_BUTTON_STYLE : SECONDARY_BUTTON_STYLE;

    const bannerMedia =
        'remoteDesktopImageUrl' in media ? (
            <Picture
                imgSrc={media.remoteDesktopImageUrl}
                imgAlt={media.webAltText}
                imageComponentProps={{
                    style: IMG_SIZE_FEATURE_BANNER,
                }}
            />
        ) : (
            <div className="feature-banner-video-container">
                <ReactPlayer
                    data-testid="video-player"
                    url={media.remoteVideoUrl}
                    onPlay={() => {
                        trackFeatureBannerClick('video', header);
                    }}
                    controls
                    width="100%"
                    height="100%"
                    playsinline
                    loop={media.isVideoLooped}
                    playing={media.isVideoAutoplay}
                    // muted required for autoplay
                    muted={media.isVideoAutoplay}
                />
            </div>
        );

    return (
        <LayoutContainer className="feature-banner-container">
            <Feature className="feature-banner" media={bannerMedia} style={style}>
                <Heading tagName={isH1 ? 'h1' : 'h2'} size="title">
                    {header}
                </Heading>
                <TextPassage>{description}</TextPassage>
                {cta && (
                    <a
                        data-testid="feature-banner-cta"
                        target={cta.behavior === NEW_TAB ? TARGET_BLANK : TARGET_SELF}
                        href={cta?.link?.webUrl}
                        onClick={() => {
                            if (cta.text) {
                                trackFeatureBannerClick(cta.text, header);
                            }
                        }}
                        rel="noreferrer"
                        className="feature-banner__link"
                    >
                        <Button variant={variant} text={cta.text} fullWidth={isMobile} size="lg" />
                    </a>
                )}
            </Feature>
        </LayoutContainer>
    );
}
